// ============================================================
// Cliente mínimo de Supabase, sin librerías.
//
// Por qué a mano y no con @supabase/supabase-js: la app usa
// autenticación por mail y contraseña y consultas REST simples.
// Eso son ~150 líneas contra una dependencia de varios cientos de
// kilobytes que hay que mantener actualizada. Si más adelante hace
// falta tiempo real o subida de archivos, ahí sí conviene la oficial.
//
// La clave anónima es pública a propósito: Supabase la diseñó así.
// Lo que protege los datos es RLS, no esconder la clave.
// ============================================================

const URL = import.meta.env?.VITE_SUPABASE_URL || "";
const ANON = import.meta.env?.VITE_SUPABASE_ANON_KEY || "";

// Si no hay configuración, la app arranca en modo demo con los
// datos de ejemplo. Permite seguir iterando el diseño sin base.
export const hayBackend = Boolean(URL && ANON);

const CLAVE_SESION = "be_sesion";
const CLAVE_MAIL = "be_mail";

// Dónde se guarda la sesión según lo que eligió el usuario:
//  - "recordar"  → localStorage: sobrevive a cerrar el navegador
//  - "esta vez"  → sessionStorage: se borra al cerrar la pestaña
// En un celular compartido o prestado, lo segundo importa.
let sesion = null;
let deposito = null;

function buscarSesion() {
  for (const d of ["localStorage", "sessionStorage"]) {
    try {
      const guardada = globalThis[d]?.getItem(CLAVE_SESION);
      if (guardada) { deposito = d; return JSON.parse(guardada); }
    } catch { /* almacenamiento bloqueado */ }
  }
  return null;
}
sesion = buscarSesion();

function guardarSesion(s, recordar) {
  sesion = s;
  if (recordar !== undefined) deposito = recordar ? "localStorage" : "sessionStorage";
  try {
    // Se limpia de los dos lados para que no queden restos cruzados
    // al cambiar de opción entre un ingreso y otro.
    globalThis.localStorage?.removeItem(CLAVE_SESION);
    globalThis.sessionStorage?.removeItem(CLAVE_SESION);
    if (s) globalThis[deposito || "localStorage"]?.setItem(CLAVE_SESION, JSON.stringify(s));
  } catch {
    // Si el navegador bloquea el almacenamiento, la sesión dura lo
    // que dure la pestaña. No es motivo para romper la app.
  }
}

// El mail se recuerda aparte: sirve para precargarlo aunque la
// persona haya elegido no mantener la sesión abierta.
export const mailRecordado = () => {
  try { return globalThis.localStorage?.getItem(CLAVE_MAIL) || ""; }
  catch { return ""; }
};

const recordarMail = (email, recordar) => {
  try {
    if (recordar) globalThis.localStorage?.setItem(CLAVE_MAIL, email);
    else globalThis.localStorage?.removeItem(CLAVE_MAIL);
  } catch { /* ignorar */ }
};

// Si la sesión quedó guardada en localStorage, es porque el usuario
// eligió que se recordara.
export const seRecuerda = () => deposito === "localStorage";

export const sesionActual = () => sesion;

function cabeceras(extra = {}) {
  return {
    apikey: ANON,
    Authorization: `Bearer ${sesion?.access_token || ANON}`,
    "Content-Type": "application/json",
    ...extra,
  };
}

async function respuesta(r) {
  const texto = await r.text();
  let cuerpo = null;
  try { cuerpo = texto ? JSON.parse(texto) : null; } catch { cuerpo = texto; }
  if (!r.ok) {
    // Postgres devuelve el detalle en `details` y `hint`: sin eso el
    // mensaje queda en "Error 400", que no dice qué columna ni por qué.
    const partes = [
      cuerpo?.error_description || cuerpo?.message || cuerpo?.msg,
      cuerpo?.details, cuerpo?.hint,
    ].filter(Boolean);
    const msg = partes.length ? partes.join(" · ") : `Error ${r.status}`;
    throw Object.assign(new Error(msg), { status: r.status, cuerpo });
  }
  return cuerpo;
}

// ---------- Autenticación ----------

export async function ingresar(email, password, recordar = true) {
  const r = await fetch(`${URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: { apikey: ANON, "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
  });
  const datos = await respuesta(r);
  guardarSesion(datos, recordar);
  recordarMail(email.trim().toLowerCase(), recordar);
  return datos;
}

export async function registrarse(email, password, metadatos = {}) {
  const r = await fetch(`${URL}/auth/v1/signup`, {
    method: "POST",
    headers: { apikey: ANON, "Content-Type": "application/json" },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      password,
      data: metadatos,   // nombre y apellido: los lee el disparador de la base
    }),
  });
  const datos = await respuesta(r);
  if (datos?.access_token) guardarSesion(datos);
  return datos;
}

export async function recuperarClave(email) {
  // El link del mail tiene que volver a ESTA dirección: si el alumno
  // entra desde el dominio nuevo y el mail lo manda al viejo, aterriza
  // en otra copia de la app sin la sesión temporal.
  const volverA = encodeURIComponent(location.origin);
  const r = await fetch(`${URL}/auth/v1/recover?redirect_to=${volverA}`, {
    method: "POST",
    headers: { apikey: ANON, "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
  return respuesta(r);
}

export function salir() {
  guardarSesion(null);
}

// El token vence al rato; esto lo renueva con el de refresco.
export async function renovarSesion() {
  if (!sesion?.refresh_token) return null;
  try {
    const r = await fetch(`${URL}/auth/v1/token?grant_type=refresh_token`, {
      method: "POST",
      headers: { apikey: ANON, "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: sesion.refresh_token }),
    });
    const datos = await respuesta(r);
    guardarSesion(datos);
    return datos;
  } catch {
    guardarSesion(null);
    return null;
  }
}

// ---------- Consultas ----------
// `consulta` acepta la sintaxis de PostgREST:
//   consulta("sesiones", { select: "*", eq: { semana_id: id }, order: "orden" })

function armarQuery({ select = "*", eq = {}, order, limit, ...resto } = {}) {
  const p = new URLSearchParams();
  p.set("select", select);
  for (const [k, v] of Object.entries(eq)) p.set(k, `eq.${v}`);
  for (const [k, v] of Object.entries(resto)) p.set(k, v);
  if (order) p.set("order", order);
  if (limit) p.set("limit", String(limit));
  return p.toString();
}

async function pedir(ruta, opciones = {}, reintento = true) {
  const r = await fetch(`${URL}/rest/v1/${ruta}`, { ...opciones, headers: cabeceras(opciones.headers) });

  // 401 casi siempre es el token vencido: renovar y reintentar una vez.
  if (r.status === 401 && reintento && sesion?.refresh_token) {
    const nueva = await renovarSesion();
    if (nueva) return pedir(ruta, opciones, false);
  }

  // 404 en una función recién creada: el servidor guarda en memoria la
  // lista de funciones y tarda unos segundos en incorporar las nuevas.
  // Mientras tanto responde "no existe". Es lo que hacía fallar la
  // primera reacción justo después de correr la migración.
  if (r.status === 404 && reintento && ruta.startsWith("rpc/")) {
    const copia = r.clone();
    const cuerpo = await copia.text().catch(() => "");
    if (cuerpo.includes("PGRST202") || cuerpo.includes("Could not find the function")) {
      await new Promise((listo) => setTimeout(listo, 900));
      return pedir(ruta, opciones, false);
    }
  }

  return respuesta(r);
}

export const leer = (tabla, opts) => pedir(`${tabla}?${armarQuery(opts)}`);

export const insertar = (tabla, filas) =>
  pedir(tabla, {
    method: "POST",
    headers: { Prefer: "return=representation" },
    body: JSON.stringify(filas),
  });

export const actualizar = async (tabla, filtro, cambios) => {
  // Un cambio vacío es siempre un error de la app, no una operación
  // válida: alguien mandó un campo con un nombre que no existe y se
  // descartó en el camino. Sin este freno la petición sale igual, la
  // base responde que todo bien, y la app avisa "guardado" sin haber
  // guardado nada. Fue exactamente lo que pasó al renombrar sesiones.
  if (!cambios || Object.keys(cambios).length === 0) {
    throw new Error("No había nada para guardar (campo desconocido)");
  }

  const filas = await pedir(`${tabla}?${armarQuery({ eq: filtro, select: "*" })}`, {
    method: "PATCH",
    headers: { Prefer: "return=representation" },
    body: JSON.stringify(cambios),
  });

  // Cero filas quiere decir que la fila no existe o que los permisos
  // no dejaron tocarla. La base no lo considera un error; para la app
  // sí lo es, porque el dato no quedó guardado.
  if (Array.isArray(filas) && filas.length === 0) {
    throw new Error("No se pudo guardar: sin permiso o la fila ya no existe");
  }
  return filas;
};

// Inserta o actualiza según exista o no la fila. Con `actualizar` a
// secas, si la fila no está la operación no falla: simplemente no
// hace nada, y la app termina avisando que guardó algo que no guardó.
export const guardarOInsertar = (tabla, filas, claveConflicto) =>
  pedir(`${tabla}?on_conflict=${claveConflicto}`, {
    method: "POST",
    headers: { Prefer: "resolution=merge-duplicates,return=representation" },
    body: JSON.stringify(filas),
  });

export const borrar = (tabla, filtro) =>
  pedir(`${tabla}?${armarQuery({ eq: filtro })}`, { method: "DELETE" });

// Llama una función guardada en la base (RPC). Se usa para lo que no
// puede calcular el navegador: la EXP, que alimenta el ranking.
export const llamarFuncion = (nombre, args = {}) =>
  pedir(`rpc/${nombre}`, { method: "POST", body: JSON.stringify(args) });

// Invitar a un alumno. La llamada va a una función del servidor
// porque crear usuarios necesita una clave que no puede estar acá.
export async function invitarAlumno({ nombre, apellido, email }) {
  const r = await fetch("/.netlify/functions/invitar", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sesion?.access_token || ""}`,
    },
    body: JSON.stringify({ nombre, apellido, email, redirectTo: location.origin }),
  });
  const cuerpo = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(cuerpo.error || "No se pudo enviar la invitación");
  return cuerpo;
}

// Un alumno invitado llega con un token en la dirección. Con eso
// puede poner su contraseña sin haber entrado nunca.
export function tokenDeInvitacion() {
  try {
    const h = new URLSearchParams(location.hash.replace(/^#/, ""));
    const tipo = h.get("type");
    if (!["invite", "recovery", "signup"].includes(tipo)) return null;
    const access = h.get("access_token");
    const refresh = h.get("refresh_token");
    if (!access) return null;
    // Se guarda la sesión temporal para poder fijar la contraseña.
    guardarSesion({ access_token: access, refresh_token: refresh }, false);
    history.replaceState(null, "", location.pathname + location.search);
    return { tipo };
  } catch { return null; }
}

// Pedir el mail de recuperación.
//
// Siempre responde bien, exista o no la cuenta: decir "ese mail no
// está registrado" le confirma a cualquiera quién usa la app.
export async function pedirRecuperacion(email) {
  const r = await fetch(`${URL}/auth/v1/recover`, {
    method: "POST",
    headers: { apikey: ANON, "Content-Type": "application/json" },
    body: JSON.stringify({
      email: String(email || "").trim().toLowerCase(),
    }),
  });
  // 429 es el límite de envíos: eso sí conviene decirlo.
  if (r.status === 429) {
    throw new Error("Esperá unos minutos antes de pedirlo de nuevo");
  }
  return true;
}

export async function fijarPassword(password) {
  const r = await fetch(`${URL}/auth/v1/user`, {
    method: "PUT",
    headers: cabeceras(),
    body: JSON.stringify({ password }),
  });
  return respuesta(r);
}

// Cambiar la contraseña desde el perfil, con la actual como control.
//
// Supabase deja cambiarla sin pedir la anterior, pero eso significa
// que un teléfono desbloqueado y olvidado en el vestuario alcanza
// para que otro se quede con la cuenta. Se verifica antes.
export async function cambiarPassword(actual, nueva) {
  // El mail se pide a la base: la sesión guardada no siempre lo trae.
  let mail = sesion?.user?.email;
  if (!mail) {
    const u = await fetch(`${URL}/auth/v1/user`, { headers: cabeceras() });
    if (u.ok) mail = (await u.json())?.email;
  }
  if (!mail) throw new Error("No se pudo identificar tu cuenta");

  // Se prueba la contraseña actual iniciando sesión con ella.
  const r = await fetch(`${URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: { apikey: ANON, "Content-Type": "application/json" },
    body: JSON.stringify({ email: mail, password: actual }),
  });
  if (!r.ok) throw new Error("La contraseña actual no es correcta");

  const nuevaSesion = await r.json();
  // Ese intento devolvió una sesión nueva: se adopta, porque la
  // anterior queda vieja y el cambio fallaría con un token vencido.
  if (nuevaSesion?.access_token) {
    guardarSesion(nuevaSesion, !!localStorage.getItem(CLAVE_SESION));
  }

  const res = await fetch(`${URL}/auth/v1/user`, {
    method: "PUT",
    headers: cabeceras(),
    body: JSON.stringify({ password: nueva }),
  });
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo?.msg || "No se pudo cambiar la contraseña");
  }
  return true;
}

// ─────────────────────────────────────────────────────────────
// Videos de los alumnos
// ─────────────────────────────────────────────────────────────

// Sube un archivo al depósito. La ruta arranca con el id del alumno:
// las reglas de la base usan esa primera carpeta para saber de quién
// es cada archivo.
export async function subirVideo(alumnoId, archivo, alProgreso) {
  const ext = (archivo.type.split("/")[1] || "mp4").replace("quicktime", "mov");
  const ruta = `${alumnoId}/${Date.now()}.${ext}`;

  // XMLHttpRequest y no fetch: es la única forma de saber cuánto
  // lleva subido, y un video sin barra de progreso parece colgado.
  await new Promise((ok, mal) => {
    const req = new XMLHttpRequest();
    req.open("POST", `${URL}/storage/v1/object/videos-alumnos/${ruta}`);
    req.setRequestHeader("apikey", ANON);
    req.setRequestHeader("Authorization", `Bearer ${sesion?.access_token || ""}`);
    req.setRequestHeader("x-upsert", "true");
    req.upload.onprogress = (e) => {
      if (e.lengthComputable && alProgreso) alProgreso(e.loaded / e.total);
    };
    req.onload = () => (req.status < 300 ? ok() : mal(new Error("No se pudo subir")));
    req.onerror = () => mal(new Error("Se cortó la conexión"));
    req.send(archivo);
  });

  return ruta;
}

// El depósito es privado, así que para verlo hace falta una dirección
// firmada que caduca. Se pide en el momento de mirarlo.
//
// No usa `pedir` porque esa función arma direcciones de la base de
// datos, y esto va al almacenamiento, que es otro camino.
async function pedirStorage(ruta, opciones = {}) {
  const r = await fetch(`${URL}/storage/v1/${ruta}`, {
    ...opciones,
    headers: {
      apikey: ANON,
      Authorization: `Bearer ${sesion?.access_token || ""}`,
      "Content-Type": "application/json",
      ...(opciones.headers || {}),
    },
  });
  if (!r.ok) {
    const e = new Error(`Almacenamiento: ${r.status}`);
    e.status = r.status;
    throw e;
  }
  return r.status === 204 ? null : r.json().catch(() => null);
}

export async function verVideo(ruta, segundos = 3600) {
  const r = await pedirStorage(`object/sign/videos-alumnos/${ruta}`, {
    method: "POST",
    body: JSON.stringify({ expiresIn: segundos }),
  });
  return r?.signedURL ? `${URL}/storage/v1${r.signedURL}` : null;
}

export const borrarVideo = (ruta) =>
  pedirStorage(`object/videos-alumnos/${ruta}`, { method: "DELETE" });


// ─────────────────────────────────────────────────────────────
// Fotos de las consultas
// ─────────────────────────────────────────────────────────────

// El coach sube en la carpeta del alumno al que le responde: así la
// foto queda con la conversación, no con quien la mandó.
export async function subirFoto(carpetaAlumno, archivo) {
  const ext = (archivo.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
  const ruta = `${carpetaAlumno}/${Date.now()}.${ext}`;
  const r = await fetch(`${URL}/storage/v1/object/fotos-consultas/${ruta}`, {
    method: "POST",
    headers: {
      apikey: ANON,
      Authorization: `Bearer ${sesion?.access_token || ""}`,
      "x-upsert": "true",
    },
    body: archivo,
  });
  if (!r.ok) throw new Error("No se pudo subir la foto");
  return ruta;
}

// Las imágenes de las insignias van a su propio bucket, público.
//
// Devuelve la dirección COMPLETA y permanente, no la ruta interna:
// eso es lo que se guarda en la base y lo que el navegador puede
// cargar. Con la ruta sola, la imagen no se veía nunca.
export async function subirImagenInsignia(archivo) {
  const ext = (archivo.type.split("/")[1] || "png").replace("jpeg", "jpg");
  const ruta = `${Date.now()}.${ext}`;
  const r = await fetch(`${URL}/storage/v1/object/insignias/${ruta}`, {
    method: "POST",
    headers: {
      apikey: ANON,
      Authorization: `Bearer ${sesion?.access_token || ""}`,
      "x-upsert": "true",
    },
    body: archivo,
  });
  if (!r.ok) {
    const detalle = await r.text().catch(() => "");
    throw new Error(detalle.includes("Bucket not found")
      ? "Falta el bucket de insignias. Corré la migración 051."
      : "No se pudo subir la imagen");
  }
  return `${URL}/storage/v1/object/public/insignias/${ruta}`;
}

export async function verFoto(ruta, segundos = 3600) {
  const r = await pedirStorage(`object/sign/fotos-consultas/${ruta}`, {
    method: "POST",
    body: JSON.stringify({ expiresIn: segundos }),
  });
  return r?.signedURL ? `${URL}/storage/v1${r.signedURL}` : null;
}

export const borrarFoto = (ruta) =>
  pedirStorage(`object/fotos-consultas/${ruta}`, { method: "DELETE" });
